import { useState, useEffect, useCallback } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Clock, CalendarDays, User, Mail, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { it as itLocale, enUS, es as esLocale } from "date-fns/locale";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DATE_LOCALES = { it: itLocale, en: enUS, es: esLocale };

export const BookingSection = () => {
  const { t, language } = useLanguage();
  const dateLocale = DATE_LOCALES[language] || itLocale;
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booked, setBooked] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    notes: "",
  });

  const fetchAvailableSlots = useCallback(async (date) => {
    setSlotsLoading(true);
    setSelectedSlot(null);
    try {
      const formattedDate = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/appointments/available?date=${formattedDate}`);
      setAvailableSlots(response.data.available_slots || []);
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Errore nel caricamento degli orari");
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, fetchAvailableSlots]);

  const handleBooking = async () => {
    if (!selectedDate || !selectedSlot || !formData.name || !formData.email) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        subscriber_email: formData.email,
        subscriber_name: formData.name,
        date: format(selectedDate, "yyyy-MM-dd"),
        time_slot: selectedSlot,
        notes: formData.notes || null,
      });
      
      setBooked(true);
      toast.success("Appuntamento confermato!");
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Errore nella prenotazione");
    } finally {
      setLoading(false);
    }
  };

  const disabledDays = [
    { before: new Date() },
    { dayOfWeek: [0, 6] }, // Weekends
  ];

  if (booked) {
    return (
      <section className="py-24 md:py-32 bg-[#e8ece6]" data-testid="booking-success">
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#2c4c3b] flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-stone-100" />
          </div>
          <h2 className="font-serif text-3xl text-stone-900 mb-4">
            {t("booking.successTitle")}
          </h2>
          <p className="text-stone-600 mb-2">
            <strong>{formData.name}</strong>, {t("booking.successText")}
          </p>
          <p className="text-[#2c4c3b] font-medium text-lg mb-8">
            {format(selectedDate, "EEEE d MMMM yyyy", { locale: dateLocale })} — {selectedSlot}
          </p>
          <p className="text-stone-500 text-sm">
            {t("booking.emailConfirm")} {formData.email}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="prenota"
      className="py-24 md:py-32 bg-[#e8ece6]"
      data-testid="booking-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs tracking-[0.2em] uppercase text-[#8a9a86] font-medium">
            {t("booking.label")}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 tracking-tight leading-tight mt-4 mb-6">
            {t("booking.title")}
          </h2>
          <p className="text-stone-600 leading-relaxed">
            {t("booking.subtitle")}
            <span className="block mt-2 text-[#2c4c3b] font-medium">
              {t("booking.premiumOnly")}
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Calendar */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-stone-200/50">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#e8ece6] flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-[#2c4c3b]" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl text-stone-900">{t("booking.selectDate")}</h3>
            </div>
            
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={disabledDays}
              locale={dateLocale}
              className="rounded-xl"
              data-testid="booking-calendar"
            />

            {/* Time Slots */}
            {selectedDate && (
              <div className="mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#e8ece6] flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#2c4c3b]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl text-stone-900">{t("booking.availableSlots")}</h3>
                </div>

                {slotsLoading && (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#2c4c3b]" />
                  </div>
                )}
                {!slotsLoading && availableSlots.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="time-slots">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedSlot(slot)}
                        className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                          selectedSlot === slot
                            ? "bg-[#2c4c3b] text-stone-100"
                            : "bg-[#e8ece6] text-stone-700 hover:bg-[#2c4c3b]/10"
                        }`}
                        data-testid={`time-slot-${slot}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
                {!slotsLoading && availableSlots.length === 0 && (
                  <p className="text-stone-500 text-sm py-4">
                    {t("booking.noSlots")}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Booking Form */}
          <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-stone-200/50">
            <h3 className="font-serif text-xl text-stone-900 mb-6">{t("booking.yourData")}</h3>

            <div className="space-y-5">
              <div>
                <label className="flex items-center gap-2 text-sm text-stone-600 mb-2">
                  <User className="w-4 h-4" strokeWidth={1.5} />
                  {t("booking.fullName")}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm"
                  placeholder={t("pricing.nameLabel")}
                  data-testid="booking-name-input"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-stone-600 mb-2">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  {t("booking.email")}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm"
                  placeholder={t("pricing.emailLabel")}
                  data-testid="booking-email-input"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-stone-600 mb-2">
                  <FileText className="w-4 h-4" strokeWidth={1.5} />
                  {t("booking.notes")}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm min-h-[100px] resize-none"
                  placeholder={t("booking.notesPlaceholder")}
                  data-testid="booking-notes-input"
                />
              </div>

              {/* Summary */}
              {selectedDate && selectedSlot && (
                <div className="bg-[#e8ece6] rounded-xl p-4 mt-6">
                  <p className="text-sm text-stone-600 mb-1">{t("booking.summary")}</p>
                  <p className="font-medium text-[#2c4c3b]">
                    {format(selectedDate, "EEEE d MMMM yyyy", { locale: dateLocale })} — {selectedSlot}
                  </p>
                </div>
              )}

              <Button
                onClick={handleBooking}
                disabled={loading || !selectedDate || !selectedSlot || !formData.name || !formData.email}
                className="w-full bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full py-6 mt-4"
                data-testid="confirm-booking-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t("booking.confirm")
                )}
              </Button>

              <p className="text-xs text-stone-500 text-center">
                {t("booking.reserved")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
